import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, MessageCircle, Mail } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";
import { IMAGES, WA_NUMBER_1, WA_NUMBER_2, waLink } from "@/data/content";

const SECTIONS = [
  {
    h: "1. Information We Collect",
    blocks: [
      { p: "Depending on how you interact with IQMIZE, we may collect:" },
      { sub: "Personal Information" },
      {
        ul: [
          "Name",
          "Parent/guardian name, where applicable",
          "Email address",
          "WhatsApp or telephone number",
          "Age or date of birth, where required for program participation",
          "Billing and payment-related information",
          "Information provided when registering for mentoring or other IQMIZE services",
        ],
      },
      { sub: "Student Information" },
      {
        p: "For students participating in IQMIZE programs, we may collect limited information necessary to provide the service, such as:",
      },
      {
        ul: [
          "Student name",
          "Age or age group",
          "Learning or program-related information voluntarily provided by the student or parent/guardian",
        ],
      },
      { p: "We do not require students to provide unnecessary personal information." },
      { sub: "Technical Information" },
      {
        p: "When you visit our website, certain technical information may be automatically collected, such as:",
      },
      {
        ul: [
          "IP address",
          "Browser and device type",
          "Operating system",
          "Pages visited",
          "Date and time of access",
          "General website usage information",
        ],
      },
    ],
  },
  {
    h: "2. How We Use Your Information",
    blocks: [
      { p: "We may use the information collected to:" },
      {
        ul: [
          "Provide access to IQMIZE products and services",
          "Process purchases and payments",
          "Provide download links and product information",
          "Respond to enquiries and customer support requests",
          "Communicate with parents, students, and customers regarding their programs",
          "Provide mentoring and training services",
          "Improve our website, products, and learning materials",
          "Maintain records of registrations and transactions",
          "Prevent fraudulent or unauthorized activity",
          "Comply with applicable legal and regulatory requirements",
        ],
      },
    ],
  },
  {
    h: "3. WhatsApp and Other Communication",
    blocks: [
      {
        p: "If you contact IQMIZE through WhatsApp, telephone, email, or other communication channels, we may use the information you provide to respond to your enquiry and provide requested services.",
      },
      {
        p: "Communication through third-party platforms such as WhatsApp is also subject to the privacy policies and terms of those platforms.",
      },
    ],
  },
  {
    h: "4. Payments",
    blocks: [
      {
        p: "Where payments are made for IQMIZE products or services, payment information may be processed through third-party payment providers.",
      },
      {
        p: "IQMIZE does not intend to store complete credit/debit card details or banking passwords on its own systems. Payment providers may collect and process information according to their own privacy policies and security practices.",
      },
    ],
  },
  {
    h: "5. Children and Students Under 18",
    blocks: [
      {
        p: "IQMIZE may provide learning materials and programs intended for students under 18 years of age. Where a student is under 18, we encourage participation and registration to be handled with the knowledge and involvement of a parent or legal guardian.",
      },
      {
        p: "Parents or legal guardians may contact us regarding information associated with their child or request appropriate corrections or deletion, subject to applicable legal requirements and legitimate business records.",
      },
    ],
  },
  {
    h: "6. Cookies and Website Technologies",
    blocks: [
      { p: "Our website may use cookies and similar technologies to:" },
      {
        ul: [
          "Maintain website functionality",
          "Understand website usage",
          "Improve user experience",
          "Remember certain preferences",
          "Measure website performance",
        ],
      },
      {
        p: "You may be able to control cookies through your browser settings. Disabling certain cookies may affect some website functionality.",
      },
    ],
  },
  {
    h: "7. Sharing of Information",
    blocks: [
      {
        p: "We do not sell or rent your personal information. We may share limited information with trusted service providers when necessary to operate IQMIZE, including providers involved in:",
      },
      {
        ul: [
          "Website hosting",
          "Payment processing",
          "Communication and messaging",
          "Email delivery",
          "Analytics",
          "Customer support",
          "Technical services",
        ],
      },
      {
        p: "Such information will be shared only to the extent reasonably necessary for the relevant service.",
      },
      {
        p: "We may also disclose information where required by applicable law, legal proceedings, government authorities, or to protect the rights, safety, and security of IQMIZE, our users, or others.",
      },
    ],
  },
  {
    h: "8. Marketing Communications",
    blocks: [
      {
        p: "If you provide your contact details and consent to receive promotional communications, IQMIZE may send information about:",
      },
      {
        ul: [
          "New products",
          "Workshops",
          "Mentoring programs",
          "Special offers",
          "Educational resources",
          "IQMIZE updates",
        ],
      },
      {
        p: "You may request to stop receiving promotional communications at any time by contacting us.",
      },
    ],
  },
  {
    h: "9. Data Security",
    blocks: [
      {
        p: "We take reasonable administrative, technical, and organizational measures to protect personal information against unauthorized access, alteration, disclosure, misuse, or destruction. However, no internet-based system can be guaranteed to be completely secure.",
      },
    ],
  },
  {
    h: "10. Data Retention",
    blocks: [
      {
        p: "We retain personal information only for as long as reasonably necessary for the purposes described in this Privacy Policy, including providing services, maintaining business and transaction records, resolving disputes, and meeting applicable legal obligations.",
      },
      {
        p: "When information is no longer required, we may delete or securely dispose of it, subject to applicable legal requirements.",
      },
    ],
  },
  {
    h: "11. Your Privacy Rights",
    blocks: [
      {
        p: "Depending on applicable law, you may have rights relating to your personal information, including the ability to:",
      },
      {
        ul: [
          "Request access to personal information we hold about you",
          "Request correction of inaccurate information",
          "Request deletion of information where legally permitted",
          "Withdraw consent for certain processing",
          "Opt out of promotional communications",
          "Raise a privacy-related complaint",
        ],
      },
      {
        p: "To exercise these rights, please contact IQMIZE using the contact details provided below.",
      },
    ],
  },
  {
    h: "12. Third-Party Websites",
    blocks: [
      {
        p: "Our website or communications may contain links to third-party websites, applications, payment services, or platforms. IQMIZE is not responsible for the privacy practices, content, or security of third-party services. We recommend reviewing their respective privacy policies before providing personal information.",
      },
    ],
  },
  {
    h: "13. Changes to This Privacy Policy",
    blocks: [
      {
        p: "IQMIZE may update this Privacy Policy from time to time to reflect changes in our services, technology, or applicable requirements. The updated version will be published on our website with a revised effective date.",
      },
    ],
  },
];

const Block = ({ block }) => {
  if (block.p) return <p className="text-base leading-relaxed text-slate-600">{block.p}</p>;
  if (block.sub) return <h3 className="text-base font-extrabold text-inavy pt-2">{block.sub}</h3>;
  if (block.ul)
    return (
      <ul className="space-y-2 pl-1">
        {block.ul.map((item) => (
          <li key={item} className="flex items-start gap-2.5">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
            <span className="text-base leading-relaxed text-slate-600">{item}</span>
          </li>
        ))}
      </ul>
    );
  return null;
};

const Privacy = () => (
  <div className="min-h-screen bg-background" data-testid="privacy-page">
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200/70 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 flex items-center justify-between h-16 lg:h-20">
        <Link to="/" data-testid="privacy-logo" className="flex items-center py-2">
          <img
            src={IMAGES.logo}
            alt="IQMIZE — Maximize Your Mind"
            className="h-10 lg:h-14 w-auto max-w-[120px] sm:max-w-[200px] object-contain object-left"
          />
        </Link>
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-1.5 text-sm font-bold text-brand hover:text-brand-dark transition-colors duration-200"
        >
          <ArrowLeft size={15} /> Back to site
        </Link>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-5 lg:px-8 py-14 lg:py-20">
      <Reveal>
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand text-white">
            <ShieldCheck size={24} strokeWidth={1.8} />
          </div>
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">IQMIZE Privacy Policy</h1>
            <p className="mt-1 text-sm font-semibold text-slate-500">Effective Date: 24 September 2026</p>
          </div>
        </div>
        <p className="mt-6 text-base leading-relaxed text-slate-600">
          IQMIZE ("IQMIZE", "we", "our", or "us") respects your privacy and is committed to protecting the personal
          information you provide when you visit our website, purchase or access our products, participate in our
          mentoring programs, or contact us through our communication channels. This Privacy Policy explains how we
          collect, use, store, and protect your information.
        </p>
      </Reveal>

      <div className="mt-12 space-y-10">
        {SECTIONS.map((s) => (
          <Reveal key={s.h}>
            <section data-testid={`privacy-section-${s.h.split(".")[0]}`}>
              <h2 className="text-xl font-bold tracking-tight text-slate-900 border-l-4 border-igold pl-4">{s.h}</h2>
              <div className="mt-4 space-y-3.5 pl-5">
                {s.blocks.map((b, i) => (
                  <Block key={i} block={b} />
                ))}
              </div>
            </section>
          </Reveal>
        ))}

        <Reveal>
          <section data-testid="privacy-section-14">
            <h2 className="text-xl font-bold tracking-tight text-slate-900 border-l-4 border-igold pl-4">14. Contact Us</h2>
            <div className="mt-4 pl-5 space-y-3.5">
              <p className="text-base leading-relaxed text-slate-600">
                If you have questions, concerns, or requests regarding this Privacy Policy or the way your information
                is handled, please contact us:
              </p>
              <div className="rounded-2xl border border-slate-200 bg-brand-light/60 p-6 space-y-3">
                <p className="text-lg font-extrabold text-inavy">IQMIZE</p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href={waLink(WA_NUMBER_1, "Hi IQMIZE! I have a privacy-related question.")}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="privacy-whatsapp-1"
                    className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-bold text-white transition-colors duration-200 hover:bg-brand-dark"
                  >
                    <MessageCircle size={15} /> 70346 18014
                  </a>
                  <a
                    href={waLink(WA_NUMBER_2, "Hi IQMIZE! I have a privacy-related question.")}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="privacy-whatsapp-2"
                    className="inline-flex items-center gap-2 rounded-full border-2 border-brand bg-white px-4 py-2 text-sm font-bold text-brand transition-colors duration-200 hover:bg-brand-light"
                  >
                    <MessageCircle size={15} /> 85905 66268
                  </a>
                </div>
                <p className="text-sm font-semibold text-slate-600">
                  Email:{" "}
                  <a href="mailto:info@iqmize.com" data-testid="privacy-email-link" className="text-brand hover:underline">
                    info@iqmize.com
                  </a>
                </p>
                <p className="text-sm font-semibold text-slate-600">
                  Website:{" "}
                  <a href="https://www.iqmize.com" className="text-brand hover:underline">
                    www.iqmize.com
                  </a>
                </p>
              </div>
            </div>
          </section>
        </Reveal>
      </div>

      <div className="mt-14 border-t border-slate-200 pt-6 flex items-center justify-between">
        <Link to="/" data-testid="privacy-bottom-back" className="inline-flex items-center gap-1.5 text-sm font-bold text-brand hover:text-brand-dark">
          <ArrowLeft size={15} /> Back to IQMIZE
        </Link>
        <p className="text-xs font-semibold text-slate-400">IQMIZE — Maximize Your Mind.</p>
      </div>
    </main>
  </div>
);

export default Privacy;
